import {SHAPE_TYPES} from '../../constant/shapes'

/**
 * @typedef {Object} ShapePathDef
 * @property {string} data - SVG path data string
 * @property {number} rawWidth - Natural (source) width of the path
 * @property {number} rawHeight - Natural (source) height of the path
 */

/**
 * Registry of SVG path definitions keyed by SHAPE_TYPES value.
 * Used by both React components (CustomPath / Shape) and the Konva
 * nodeFactory (createKonvaNode) so that path data lives in one place.
 *
 * @type {Record<string, ShapePathDef>}
 */
export const SHAPE_PATHS = {
    // ── Basic ──────────────────────────────────────────────────────────
    [SHAPE_TYPES.SQUARE]: {
        data: 'M0 0H56V56H0V0Z',
        rawWidth: 56,
        rawHeight: 56,
    },
    [SHAPE_TYPES.CIRCLE]: {
        data: 'M56 28C56 43.464 43.464 56 28 56C12.536 56 0 43.464 0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28Z',
        rawWidth: 56,
        rawHeight: 56,
    },
    [SHAPE_TYPES.TRIANGLE]: {
        data: 'M32 0L64 56H0L32 0Z',
        rawWidth: 64,
        rawHeight: 56,
    },
    [SHAPE_TYPES.PENTAGON]: {
        data: 'M32 0L64 22.918L51.7771 60H12.2229L0 22.918L32 0Z',
        rawWidth: 64,
        rawHeight: 60,
    },
    [SHAPE_TYPES.HEXAGON]: {
        data: 'M27.7128 0L55.4256 16V48L27.7128 64L0 48V16L27.7128 0Z',
        rawWidth: 55.43,
        rawHeight: 64,
    },
    [SHAPE_TYPES.ROUND_RECT]: {
        data: 'M0 20C0 8.95431 8.95431 0 20 0H36C47.0457 0 56 8.95431 56 20V20C56 31.0457 47.0457 40 36 40H20C8.95431 40 0 31.0457 0 20V20Z',
        rawWidth: 56,
        rawHeight: 40,
    },
    [SHAPE_TYPES.STAR]: {
        data: 'M34.0918 20.0381H55.1621L38.1162 32.4229L44.627 52.4619L27.5811 40.0771L10.5352 52.4619L17.0459 32.4229L0 20.0381H21.0703L27.5811 0L34.0918 20.0381Z',
        rawWidth: 55.16,
        rawHeight: 52.46,
    },
    [SHAPE_TYPES.PLUS]: {
        data: 'M40 16H56V40H40V56H16V40H0V16H16V0H40V16Z',
        rawWidth: 56,
        rawHeight: 56,
    },
    [SHAPE_TYPES.TAG_LEFT]: {
        data: 'M10.5507 0H56V32H10.5507L0 16L10.5507 0Z',
        rawWidth: 56,
        rawHeight: 32,
    },
    [SHAPE_TYPES.TAG_RIGHT]: {
        data: 'M45.4493 32L0 32L0 0L45.4493 0L56 16L45.4493 32Z',
        rawWidth: 56,
        rawHeight: 32,
    },
    [SHAPE_TYPES.CHEVRON_LEFT]: {
        data: 'M10.5507 0H56L46 16L56 32H10.5507L0 16L10.5507 0Z',
        rawWidth: 56,
        rawHeight: 32,
    },
    [SHAPE_TYPES.CHEVRON_RIGHT]: {
        data: 'M10.5507 0H56L46 16L56 32H10.5507L0 16L10.5507 0Z',
        rawWidth: 56,
        rawHeight: 32,
    },
    [SHAPE_TYPES.ARROW_LEFT]: {
        data: 'M22.4071 8.38286H56V35.456H22.4071V44L0 21.9194L22.4071 0V8.38286Z',
        rawWidth: 56,
        rawHeight: 44,
    },
    [SHAPE_TYPES.ARROW_RIGHT]: {
        data: 'M33.5929 35.6171L0 35.6171L0 8.54405L33.5929 8.54405L33.5929 0L56 22.0806L33.5929 44V35.6171Z',
        rawWidth: 56,
        rawHeight: 44,
    },

    // ── Cutout ─────────────────────────────────────────────────────────
    [SHAPE_TYPES.SQUARE_CUTOUT]: {
        data: 'M56 0V56H0V0H56ZM12 44H44V12H12V44Z',
        rawWidth: 56,
        rawHeight: 56,
    },
    [SHAPE_TYPES.CIRCLE_CUTOUT]: {
        data: 'M44 28C44 19.1634 36.8366 12 28 12C19.1634 12 12 19.1634 12 28C12 36.8366 19.1634 44 28 44V56C12.536 56 0 43.464 0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56V44C36.8366 44 44 36.8366 44 28Z',
        rawWidth: 56,
        rawHeight: 56,
    },
    [SHAPE_TYPES.ROUND_RECT_CUTOUT]: {
        data: 'M36 32V40H20V32H36ZM48 20C48 13.3726 42.6274 8 36 8H20C13.3726 8 8 13.3726 8 20C8 26.6274 13.3726 32 20 32V40C8.95431 40 0 31.0457 0 20C0 8.95431 8.95431 2.57703e-07 20 0H36L36.5166 0.00683594C47.3235 0.280845 56 9.12703 56 20C56 30.873 47.3235 39.7192 36.5166 39.9932L36 40V32C42.6274 32 48 26.6274 48 20Z',
        rawWidth: 56,
        rawHeight: 40,
    },
    [SHAPE_TYPES.STAR_CUTOUT]: {
        data: 'M34.0918 20.0381H55.1621L38.1162 32.4229L44.627 52.4619L27.5811 40.0771L10.5352 52.4619L17.0459 32.4229L0 20.0381H21.0703L27.5811 0L34.0918 20.0381ZM23.9766 24.0381H12.3105L21.748 30.8955L18.1436 41.9883L27.5811 35.1328L37.0176 41.9883L33.4141 30.8955L42.8516 24.0381H31.1855L27.5811 12.9434L23.9766 24.0381Z',
        rawWidth: 55.16,
        rawHeight: 52.46,
    },
    [SHAPE_TYPES.TAG_LEFT_CUTOUT]: {
        data: 'M56 0V32H10.5508L0 16L10.5508 0H56ZM9.58301 16L14.8584 24H48V8H14.8584L9.58301 16Z',
        rawWidth: 56,
        rawHeight: 32,
    },
    [SHAPE_TYPES.TAG_RIGHT_CUTOUT]: {
        data: 'M0 32L0 0L45.4492 0L56 16L45.4492 32L0 32ZM46.417 16L41.1416 8L8 8V24L41.1416 24L46.417 16Z',
        rawWidth: 56,
        rawHeight: 32,
    },
    [SHAPE_TYPES.CHEVRON_LEFT_CUTOUT]: {
        data: 'M46 16L56 32H10.5508L0 16L10.5508 0H56L46 16ZM9.58301 16L14.8584 24H41.5654L36.5664 16L41.5654 8H14.8584L9.58301 16Z',
        rawWidth: 56,
        rawHeight: 32,
    },
    [SHAPE_TYPES.CHEVRON_RIGHT_CUTOUT]: {
        data: 'M10 16L0 0L45.4492 0L56 16L45.4492 32L0 32L10 16ZM46.417 16L41.1416 8H14.4346L19.4336 16L14.4346 24L41.1416 24L46.417 16Z',
        rawWidth: 56,
        rawHeight: 32,
    },
    [SHAPE_TYPES.ARROW_LEFT_CUTOUT]: {
        data: 'M22.4072 8.38281H56V35.4561H22.4072V44L0 21.9189L22.4072 0V8.38281ZM11.418 21.9395L17.0156 27.4561H48V16.3828H17.0996L11.418 21.9395Z',
        rawWidth: 56,
        rawHeight: 44,
    },
    [SHAPE_TYPES.ARROW_RIGHT_CUTOUT]: {
        data: 'M33.5928 35.6172L0 35.6172L0 8.54395L33.5928 8.54395L33.5928 0L56 22.0811L33.5928 44V35.6172ZM44.582 22.0605L38.9844 16.5439L8 16.5439V27.6172L38.9004 27.6172L44.582 22.0605Z',
        rawWidth: 56,
        rawHeight: 44,
    },
}
